import * as Sentry from '@sentry/angular';
import { environment } from '@/environments/environment';

/**
 * Internal utilities for Sentry processing.
 * Exported for testing purposes.
 */
export const SentryInternalUtils = {
  getHostname: () => window.location.hostname,
};

export const processSentryLocalhost = (
  event: Sentry.ErrorEvent,
  _hint: Sentry.EventHint
): Sentry.ErrorEvent | null => {
  console.log('sentry.processSentryLocalhost  ');
  // Respect the feature flag for Sentry on localhost if it's explicitly enabled
  // Note: Since this runs before the app is fully bootstrapped or in a context where
  // we might not have the service, we check localStorage or environment directly.
  const storedFlags = JSON.parse(localStorage?.getItem('FLAGS') || '{}');
  const envPlatform = environment.platform.toLowerCase();
  const localhostFlag =
    storedFlags[envPlatform]?.flagSentryOnLocalhost ??
    environment.flagSentryOnLocalhost;

  if (localhostFlag === true) {
    return event;
  }

  // Don't send events to Sentry when running on localhost
  const hostname = SentryInternalUtils.getHostname();
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.warn('Sentry event blocked on localhost', event);
    return null;
  }
  console.log(
    'sentry.processSentryLocalhost  allowing event from non-localhost',
    { hostname, event }
  );
  return event;
};

export const processSentryTransientHttpErrors = (
  event: Sentry.ErrorEvent,
  _hint: Sentry.EventHint
): Sentry.ErrorEvent => {
  // Tag transient network errors so they're identifiable in Sentry.
  // These are TypeError instances thrown by the browser's native fetch() API
  // when the request physically cannot complete (offline, DNS failure, etc.).
  // By the time these reach Sentry, retryBeforeThrow has already retried 3x.
  const exception = _hint?.originalException;
  if (
    exception instanceof TypeError &&
    (exception.message === 'Failed to fetch' ||
      exception.message === 'NetworkError when attempting to fetch resource' ||
      exception.message === 'Load failed')
  ) {
    event.tags = event.tags || {};
    event.tags.retries_exhausted = 'true';
    event.tags.error_type = 'transient_network';
    event.level = 'warning';
    console.log(
      'main.beforeSend transient string tagging transient network error',
      event.tags
    );
  }

  // Tag HttpErrorResponse status-0 and status-5xx errors.
  // We check both the top-level event message (for captureMessage)
  // and the exception values (for captureException).
  const message = event.message || event.exception?.values?.[0]?.value || '';
  const errorObj = exception as any;

  if (
    message.includes('Http failure response for') ||
    (errorObj && errorObj.name === 'HttpErrorResponse') ||
    (errorObj && errorObj.status === 0)
  ) {
    if (message.includes(': 0 ') || (errorObj && errorObj.status === 0)) {
      event.tags = event.tags || {};
      event.tags.retries_exhausted = 'true';
      event.tags.error_type = 'transient_network';
      event.level = 'warning';
      console.log(
        'main.beforeSend status 0 tagging transient network error',
        event.tags
      );
    } else if (
      message.match(/: 5\d\d /) ||
      (errorObj && errorObj.status >= 500)
    ) {
      event.tags = event.tags || {};
      event.tags.retries_exhausted = 'true';
      event.tags.error_type = 'server_error';
      console.log(
        'main.beforeSend status 5xx tagging server error',
        event.tags
      );
    }
  }

  return event;
};

export const processSentryHttpErrorFingerprint = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent => {
  console.log('sentry.processSentryHttpErrorFingerprint  ', event, hint);
  const message = event.exception?.values?.[0]?.value ?? '';
  const match = message.match(/Http failure response for (.+): (\d+)/);
  if (match) {
    console.log(
      'sentry.processSentryHttpErrorFingerprint  matched HTTP error pattern'
    );
    try {
      const url = new URL(match[1]).pathname;
      const status = match[2];
      event.fingerprint = ['http-error', url, status];
    } catch {
      // If URL parsing fails, fall back to the raw match
      event.fingerprint = ['http-error', match[1], match[2]];
    }
  }
  return event;
};
